    // src/firebase/businessService.js
    // ✅ Last updated: 2026-04-21 (America/Puerto_Rico)
    /* eslint-disable no-unused-vars */

    import {
        addDoc,
        arrayRemove,
        arrayUnion,
        collection,
        deleteDoc,
        doc,
        documentId,
        getDoc,
        getDocs,
        increment,
        limit as fsLimit,
        onSnapshot,
        orderBy,
        query,
        serverTimestamp,
        setDoc,
        startAfter as fsStartAfter,
        startAt,
        endAt,
        updateDoc,
        where,
    } from "firebase/firestore";

    import {
        getDownloadURL,
        ref as storageRef,
        uploadBytesResumable,
        deleteObject,
        listAll,
    } from "firebase/storage";

    import { db, auth, storage, IS_EMU } from "./firebase.jsx";
import { logAuditEvent } from "../translations/utils/auditLogger.js";
    import { normalizeCategory } from "../data/categoryOptions.js";

    /* ───────────────────────────── config ───────────────────────────── */
    const STORAGE_ENABLED = true;

    /**
     * ✅ Publishing / Review Rules
     * - Default: businesses are NOT public unless published === true
     * - Clicking "Publish" from the business editor makes the business public immediately
     * - Clicking "Unpublish" sends it back to owner draft
     * - Review queue is NOT required for normal publishing
     * - Admin/Mod approval helpers are still available when needed
     * - Rejection still makes the business non-public
     */

    /* ───────────────────────────── collections ───────────────────────────── */
    export const BUSINESSES_COLLECTION = "businesses";
    const CLAIMS_COLLECTION = "claimedBusinesses";
    const USERS_COLLECTION = "users";
    const OWNER_VERIFICATION_COLLECTION = "ownerVerificationRequests";
    const REPORTS_COLLECTION = "reports";
    const USER_FAVORITES_COLLECTION = "userFavorites";
    const SYSTEM_SETTINGS_DOC = doc(db, "systemSettings", "global");
    const BUSINESS_REVIEWS_SUBCOLLECTION = "reviews";

    /* ───────────────────────────── small helpers ───────────────────────────── */
    const str = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));
    const lower = (v) => str(v).toLowerCase();
    const safeTrim = (v) => String(v || "").trim();
    const isHttpUrl = (u = "") => /^https?:\/\//i.test(String(u || "").trim());

    const col = (name) => collection(db, name);
    const dref = (name, id) => doc(db, name, id);
    const mapDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    /**
     * ✅ Performance fix:
     * Use normal Firestore reads instead of forcing server-only reads.
     * This reduces cold-start waiting and allows Firestore to behave more efficiently.
     */
    const getDocSafe = (r) => getDoc(r);
    const getDocsSafe = (q) => getDocs(q);

    const isPermDenied = (e) =>
        String(e?.code || e?.message || e || "").includes("permission-denied");
    const isMaintenance = (e) =>
        String(e?.code || e?.message || e || "").includes("maintenance-mode");

    function explainError(e) {
        const code = e?.code || "";
        const msg = e?.message || String(e || "");
        if (isMaintenance(e)) return "Maintenance mode is ON (writes are blocked).";
        if (isPermDenied(e)) return "Permission denied by Firestore rules.";
        if (code) return `${code}: ${msg}`;
        return msg || "Unknown error";
    }

    function toNumber(value, fallback = 0) {
        const n = Number(value);
        return Number.isFinite(n) ? n : fallback;
    }

    function normalizeRating(value) {
        const n = Number(value);
        if (!Number.isFinite(n)) return 0;
        return Math.max(1, Math.min(5, Math.round(n)));
    }

    function businessRef(businessId) {
        return dref(BUSINESSES_COLLECTION, businessId);
    }

    function businessReviewsCol(businessId) {
        return collection(db, BUSINESSES_COLLECTION, businessId, BUSINESS_REVIEWS_SUBCOLLECTION);
    }

    function businessReviewRef(businessId, reviewId) {
        return doc(db, BUSINESSES_COLLECTION, businessId, BUSINESS_REVIEWS_SUBCOLLECTION, reviewId);
    }

    /**
     * Some reads (like users/* or claimedBusinesses/*) may be blocked by rules.
     * These wrappers prevent permission-denied from breaking flows.
     */
    async function tryGetDoc(refOrDocRef) {
        try {
            return await getDocSafe(refOrDocRef);
        } catch (e) {
            if (isPermDenied(e)) return null;
            throw e;
        }
    }

    async function tryGetDocs(qRef) {
        try {
            return await getDocsSafe(qRef);
        } catch (e) {
            if (isPermDenied(e)) return null;
            throw e;
        }
    }

    const chunk = (arr, n = 10) =>
        Array.from({ length: Math.ceil(arr.length / n) }, (_, i) =>
            arr.slice(i * n, i * n + n)
        );

    const normalizeRolesArray = (input) => {
        if (!input) return [];
        if (Array.isArray(input)) return input.filter(Boolean).map(String);
        if (typeof input === "string") return [input];
        if (typeof input === "object") return Object.keys(input).filter((k) => !!input[k]);
        return [];
    };

    /* ───────────────────────────── role helper ───────────────────────────── */
    const _rolesCache = new Map();
    const ROLES_TTL_MS = 10 * 1000;

    function getRolesCache(uid) {
        const key = str(uid).trim();
        if (!key) return null;
        const hit = _rolesCache.get(key);
        if (!hit) return null;
        if (hit.exp <= Date.now()) {
            _rolesCache.delete(key);
            return null;
        }
        return hit.value;
    }

    function setRolesCache(uid, roles) {
        const key = str(uid).trim();
        if (!key) return roles;
        _rolesCache.set(key, {
            value: roles,
            exp: Date.now() + ROLES_TTL_MS,
        });
        return roles;
    }

    function clearRolesCache(uid = auth.currentUser?.uid) {
        const key = str(uid).trim();
        if (!key) return;
        _rolesCache.delete(key);
    }

    async function getCurrentUserRolesSafe() {
        try {
            const uid = auth.currentUser?.uid;
            if (!uid) return [];

            const cached = getRolesCache(uid);
            if (cached) return cached;

            const uSnap = await tryGetDoc(dref(USERS_COLLECTION, uid));
            const uData = uSnap?.exists?.() ? uSnap.data() : {};
            const roles = normalizeRolesArray(uData?.roles || uData?.role)
                .map((r) => lower(r))
                .filter((r) => r && r !== "government");

            return setRolesCache(uid, roles);
        } catch {
            return [];
        }
    }

    async function isAdminOrMod() {
        const roles = await getCurrentUserRolesSafe();
        return roles.includes("admin") || roles.includes("moderator") || roles.includes("mod");
    }

    /* ───────────────────────────── defaults / normalize ───────────────────────────── */
    const DEFAULT_MODULES = {
        meetTeam: false,
        featuredOffers: true,
        eventCalendar: false,
    };

    const sanitizeModules = (m = {}) => ({
        meetTeam: !!m.meetTeam,
        featuredOffers: m.featuredOffers ?? true,
        eventCalendar: !!m.eventCalendar,
    });

    const normalizeCountryCode = (v) => {
        const raw = str(v).trim();
        if (!raw) return "";

        const upper = raw.toUpperCase().trim();
        if (/^[A-Z]{2}$/.test(upper)) return upper;

        const MAP = {
            USA: "US",
            "UNITED STATES": "US",
            "UNITED STATES OF AMERICA": "US",
            "U.S.": "US",
            "U.S.A.": "US",
            US: "US",
            CAN: "CA",
            CANADA: "CA",
            "PUERTO RICO": "PR",
            PR: "PR",
        };

        if (MAP[upper]) return MAP[upper];

        const cleaned = upper.replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
        if (MAP[cleaned]) return MAP[cleaned];

        const first = cleaned.split(/[\s,]+/)[0] || "";
        if (MAP[first]) return first;
        if (/^[A-Z]{2}$/.test(first)) return first;

        return "";
    };

    const normalizeVerification = (v = {}) => {
        const status = lower(v?.status).trim() || "unverified";
        const source = lower(v?.source).trim() || "self_reported";
        const allowed = new Set(["unverified", "pending", "self_verified", "trusted", "rejected"]);

        return {
            status: allowed.has(status) ? status : "unverified",
            source,
            notes: str(v?.notes),
            updatedAt: v?.updatedAt ?? null,
        };
    };

    export function withDefaults(biz) {
        if (!biz) return biz;

        const published = typeof biz.published === "boolean" ? biz.published : false;

        const status =
            biz.status ||
            (published === true
                ? "published"
                : biz.reviewStatus === "rejected"
                    ? "rejected"
                    : biz.paused
                        ? "paused"
                        : biz.verified
                            ? "verified"
                            : biz.ownerUpdated || biz.owner_override
                                ? "owner_updated"
                                : "owner_draft");

        return {
            ...biz,
            category: normalizeCategory(biz.category, { allowOther: true }),
            verification: normalizeVerification(biz.verification),
            phone: biz.phone ?? biz.contact ?? "",
            isOpenNow: biz.isOpenNow ?? biz.openNow ?? false,
            openNow: biz.openNow ?? biz.isOpenNow ?? false,
            country: normalizeCountryCode(biz.country) || "US",
            accentColor: biz.accentColor || "#1E4D8B",
            modules: sanitizeModules(biz.modules ?? DEFAULT_MODULES),
            status,
            published,
            viewCount: toNumber(biz.viewCount),
            saveCount: toNumber(biz.saveCount),
            averageRating: toNumber(biz.averageRating),
            reviewCount: toNumber(biz.reviewCount),
        };
    }

    /* ───────────────────────────── generic subscribe helpers ───────────────────────────── */
    export function subscribeDoc(docRef, onNext, onError) {
        return onSnapshot(
            docRef,
            (snap) => {
                try {
                    onNext?.(snap);
                } catch { }
            },
            (err) => {
                try {
                    onError?.(err);
                } catch { }
            }
        );
    }

    export function subscribeQuery(qRef, onNext, onError) {
        return onSnapshot(
            qRef,
            (snap) => {
                try {
                    onNext?.(snap);
                } catch { }
            },
            (err) => {
                try {
                    onError?.(err);
                } catch { }
            }
        );
    }

    /* ───────────────────────────── image URL helpers ───────────────────────────── */
    const _urlCache = new Map();

    function addCacheBust(url = "", version = Date.now()) {
        const clean = String(url || "").trim();
        if (!clean) return "";
        return `${clean}${clean.includes("?") ? "&" : "?"}v=${version}`;
    }

    export async function cachedUrl(pathOrUrl, { ttlMs = 15 * 60 * 1000, force = false } = {}) {
        if (!pathOrUrl) return "";
        if (isHttpUrl(pathOrUrl)) return pathOrUrl;
        if (!STORAGE_ENABLED) return "";

        const now = Date.now();
        const hit = _urlCache.get(pathOrUrl);
        if (!force && hit && hit.exp > now && hit.url) return hit.url;

        try {
            const rawUrl = await getDownloadURL(storageRef(storage, pathOrUrl));
            const freshUrl = addCacheBust(rawUrl, now);
            _urlCache.set(pathOrUrl, { url: freshUrl, exp: now + ttlMs });
            return freshUrl;
        } catch {
            _urlCache.delete(pathOrUrl);
            return "";
        }
    }

    export const urlFromPathOrUrl = cachedUrl;

    export function invalidateCachedUrl(pathOrUrl) {
        if (!pathOrUrl || isHttpUrl(pathOrUrl)) return;
        _urlCache.delete(pathOrUrl);
    }

    export async function refreshCachedUrl(path) {
        return cachedUrl(path, { force: true });
    }

    /* ───────────────────────────── maintenance ───────────────────────────── */
    async function assertNotInMaintenance() {
        const snap = await getDocSafe(SYSTEM_SETTINGS_DOC);
        const data = snap.exists() ? snap.data() : {};
        if (data.maintenanceMode === true) {
            const err = new Error("maintenance-mode");
            err.code = "maintenance-mode";
            throw err;
        }
    }

    export async function getMaintenanceMode() {
        const snap = await getDocSafe(SYSTEM_SETTINGS_DOC);
        const data = snap.exists() ? snap.data() : {};
        return { exists: snap.exists(), data, maintenanceMode: data.maintenanceMode === true };
    }

    export async function setMaintenanceMode(value) {
        await setDoc(
            SYSTEM_SETTINGS_DOC,
            { maintenanceMode: !!value, updatedAt: serverTimestamp() },
            { merge: true }
        );
        return { ok: true, maintenanceMode: !!value };
    }

    /* ───────────────────────────── admin: users / roles ───────────────────────────── */
    export async function getAllUsers({ limit = 1000 } = {}) {
        try {
            const qRef = query(col(USERS_COLLECTION), fsLimit(limit));
            const snap = await getDocsSafe(qRef);

            return snap.docs.map((d) => {
                const v = d.data() || {};
                const roles = normalizeRolesArray(v.roles || v.role).filter(
                    (r) => lower(r) !== "government"
                );
                return {
                    id: d.id,
                    email: v.email || "",
                    roles,
                    roleSource: "users",
                };
            });
        } catch (e) {
            if (isPermDenied(e)) return [];
            throw e;
        }
    }

    export async function getCurrentUserProfile() {
        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in.");

        const userRef = dref(USERS_COLLECTION, user.uid);
        const snap = await getDocSafe(userRef);

        if (!snap.exists()) {
            return {
                uid: user.uid,
                email: lower(user.email),
                roles: [],
                raw: null,
            };
        }

        const data = snap.data() || {};
        const roles = normalizeRolesArray(data.roles || data.role)
            .map((r) => lower(r))
            .filter(Boolean);

        setRolesCache(user.uid, roles);

        return {
            uid: user.uid,
            email: lower(user.email),
            roles,
            raw: data,
        };
    }

    export async function grantOwnerAccessAfterPayment() {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in.");

        const uid = user.uid;
        const email = lower(user.email);

        const profile = await getCurrentUserProfile();
        const currentRoles = Array.isArray(profile.roles) ? profile.roles : [];

        const nextRoles = [...new Set([...currentRoles, "owner"])].filter(
            (r) => r && r !== "government"
        );

        await setDoc(
            dref(USERS_COLLECTION, uid),
            {
                email,
                roles: nextRoles,
                role: "owner",
                updatedAt: serverTimestamp(),
                ownerAccessPaid: true,
                ownerAccessPaidAt: serverTimestamp(),
            },
            { merge: true }
        );

        clearRolesCache(uid);

        try {
            await logAuditEvent("owner_access_granted_after_payment", {
                targetUid: uid,
                targetEmail: email,
                roles: nextRoles,
                actorUid: uid,
                actorEmail: email,
            });
        } catch { }

        const myBusinesses = await getMyBusinesses();

        if (myBusinesses.length > 0) {
            const firstBusiness = myBusinesses[0];

            try {
                await updateDoc(dref(BUSINESSES_COLLECTION, firstBusiness.id), {
                    ownerUid: uid,
                    claimedBy: email,
                    updatedAt: serverTimestamp(),
                });
            } catch { }

            try {
                await ensureClaimOrOwnership(firstBusiness.id);
            } catch { }

            return {
                ok: true,
                created: false,
                businessId: firstBusiness.id,
                redirectTo: "/dashboard",
                roles: nextRoles,
            };
        }

        const created = await createBusinessForOwner({
            email,
            ownerUid: uid,
            claimedBy: email,
            infoCompleted: false,
            status: "owner_draft",
            published: false,
            needsReview: false,
            reviewStatus: "draft",
        });

        try {
            await updateDoc(dref(BUSINESSES_COLLECTION, created.id), {
                ownerUid: uid,
                claimedBy: email,
                updatedAt: serverTimestamp(),
            });
        } catch { }

        try {
            await ensureClaimOrOwnership(created.id);
        } catch { }

        return {
            ok: true,
            created: true,
            businessId: created.id,
            redirectTo: "/dashboard",
            roles: nextRoles,
        };
    }

    export async function updateUserRole(uid, roles = []) {
        await assertNotInMaintenance();
        if (!uid) throw new Error("Missing uid");

        const clean = normalizeRolesArray(roles).filter((r) => lower(r) !== "government");
        const userRef = dref(USERS_COLLECTION, uid);

        try {
            await setDoc(userRef, { roles: clean, updatedAt: serverTimestamp() }, { merge: true });
            clearRolesCache(uid);

            try {
                await logAuditEvent("role_change", {
                    targetUid: uid,
                    roles: clean,
                    actorUid: auth.currentUser?.uid || null,
                    actorEmail: auth.currentUser?.email || null,
                });
            } catch { }

            return { ok: true, uid, roles: clean };
        } catch (e) {
            if (isPermDenied(e)) throw new Error("Permission denied by Firestore rules.");
            throw e;
        }
    }

    /* ───────────────────────────── BUSINESS CRUD ───────────────────────────── */
    export async function updateBusinessById(idOrRef, updates) {
        await assertNotInMaintenance();

        const r = typeof idOrRef === "string" ? dref(BUSINESSES_COLLECTION, idOrRef) : idOrRef;
        const snap = await getDocSafe(r);
        if (!snap.exists()) throw new Error("Business not found");

        const current = snap.data() || {};
        const adminMod = await isAdminOrMod();

        const cleanUpdates = { ...(updates || {}) };

        if (typeof cleanUpdates.name === "string") {
            const trimmedName = cleanUpdates.name.trim();
            cleanUpdates.name = trimmedName;
            cleanUpdates.nameLower = trimmedName.toLowerCase();
        }

        if ("category" in cleanUpdates) {
            cleanUpdates.category = normalizeCategory(cleanUpdates.category, { allowOther: true });
        }

        if ("modules" in cleanUpdates) {
            cleanUpdates.modules = sanitizeModules(cleanUpdates.modules ?? current.modules ?? DEFAULT_MODULES);
        }

        if ("openNow" in cleanUpdates && !("isOpenNow" in cleanUpdates)) {
            cleanUpdates.isOpenNow = !!cleanUpdates.openNow;
        }
        if ("isOpenNow" in cleanUpdates && !("openNow" in cleanUpdates)) {
            cleanUpdates.openNow = !!cleanUpdates.isOpenNow;
        }

        if (adminMod && cleanUpdates?.verification) {
            cleanUpdates.verification = normalizeVerification({
                ...normalizeVerification(current?.verification),
                ...cleanUpdates.verification,
                updatedAt: serverTimestamp(),
            });
        }

        if (!adminMod) {
            const adminOnlyKeys = [
                "verified",
                "verification",
                "approvedAt",
                "approvedBy",
                "approvedByEmail",
                "rejectedAt",
                "rejectedBy",
                "rejectedByEmail",
                "ownerUid",
                "claimedBy",
                "claimedAt",
                "revokedAt",
                "revokedByUid",
                "revokedByEmail",
                "viewCount",
                "saveCount",
                "averageRating",
                "reviewCount",
            ];

            for (const key of adminOnlyKeys) {
                delete cleanUpdates[key];
            }

            const ownerSafeUpdates = {
                ...cleanUpdates,
                updatedAt: serverTimestamp(),
            };

            try {
                await updateDoc(r, ownerSafeUpdates);
                return { id: snap.id, ...current, ...ownerSafeUpdates };
            } catch (e) {
                if (isPermDenied(e)) throw new Error("You don't have permission to update this business.");
                throw e;
            }
        }

        const nextStatus = str(cleanUpdates?.status || "").toLowerCase().trim();

        const statusLooksDraft =
            nextStatus.includes("draft") ||
            nextStatus === "owner_draft" ||
            nextStatus.includes("under_review") ||
            nextStatus.includes("pending_review");

        const nextPublished =
            "published" in cleanUpdates
                ? !!cleanUpdates.published
                : statusLooksDraft
                    ? false
                    : typeof current.published === "boolean"
                        ? current.published
                        : false;

        const finalUpdates = {
            ...cleanUpdates,
            published: nextPublished,
            lastOwnerUpdate: serverTimestamp(),
            ownerUpdated: true,
            status: cleanUpdates.status || current.status || "owner_updated",
            updatedAt: serverTimestamp(),
        };

        try {
            await updateDoc(r, finalUpdates);
            return { id: snap.id, ...current, ...finalUpdates };
        } catch (e) {
            if (isPermDenied(e)) throw new Error("You don't have permission to update this business.");
            throw e;
        }
    }

    export async function saveBusinessDraftById(businessId, updates = {}) {
        if (!businessId) throw new Error("Missing business id");
        const payload = {
            ...updates,
            published: false,
            status: updates.status || "owner_draft",
            reviewStatus: updates.reviewStatus || "draft",
            needsReview: false,
            updatedAt: serverTimestamp(),
        };
        return updateBusinessById(businessId, payload);
    }

    export async function createBusinessForOwner(partial = {}) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in to create a business.");

        const uid = user.uid;
        const email = lower(user.email);
        const now = serverTimestamp();

        const base = {
            name: "",
            nameLower: "",
            category: "",
            state: "",
            county: "",
            town: "",
            zipCode: "",
            address: "",
            phone: "",
            email,
            website: "",
            description: "",
            country: "US",

            ownerUid: uid,
            claimedBy: email || null,

            verified: false,
            ownerUpdated: false,
            infoCompleted: false,

            status: "owner_draft",
            published: false,

            needsReview: false,
            reviewStatus: "draft",
            reviewReason: "",

            verification: {
                status: "unverified",
                source: "self_reported",
                notes: "",
                updatedAt: now,
            },

            accentColor: "#1E4D8B",
            modules: sanitizeModules(partial.modules ?? DEFAULT_MODULES),

            viewCount: 0,
            saveCount: 0,
            averageRating: 0,
            reviewCount: 0,

            createdAt: now,
            updatedAt: now,
        };

        const data = {
            ...base,
            ...partial,
            category: normalizeCategory(partial.category, { allowOther: true }) || "",
            viewCount: toNumber(partial.viewCount, 0),
            saveCount: toNumber(partial.saveCount, 0),
            averageRating: toNumber(partial.averageRating, 0),
            reviewCount: toNumber(partial.reviewCount, 0),
        };

        const docRef = await addDoc(col(BUSINESSES_COLLECTION), data);

        try {
            await setDoc(
                dref(CLAIMS_COLLECTION, docRef.id),
                { uid, claimedBy: email, claimedAt: serverTimestamp(), revokedAt: null },
                { merge: true }
            );
        } catch { }

        try {
            await logAuditEvent("business_created_by_owner", {
                businessId: docRef.id,
                ownerUid: uid,
                ownerEmail: email,
            });
        } catch { }

        return withDefaults({ id: docRef.id, ...data });
    }

    export const setAppearanceAndModules = (businessId, { accentColor, modules }) =>
        updateBusinessById(businessId, {
            accentColor: accentColor || "#1E4D8B",
            modules: sanitizeModules(modules),
        });

    /* ───────────────────────────── publish / review helpers ───────────────────────────── */
    export async function setBusinessPublished(businessId, published = true) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing business id");

        const r = dref(BUSINESSES_COLLECTION, businessId);
        const snap = await getDocSafe(r);
        if (!snap.exists()) throw new Error("Business not found");

        const biz = snap.data() || {};
        const businessName = biz.name || biz.businessName || "";

        const wantOn = !!published;

        if (wantOn) {
            await updateDoc(r, {
                published: false,
                needsReview: true,
                reviewStatus: "pending_review",
                reviewReason: "",
                status: "pending_review",
                submittedForReviewAt: serverTimestamp(),
                submittedForReviewBy: auth.currentUser?.uid || null,
                submittedForReviewByEmail: auth.currentUser?.email || null,
                updatedAt: serverTimestamp(),
            });

            try {
                await logAuditEvent("business_submitted_for_review", {
                    businessId,
                    businessName,
                    uid: auth.currentUser?.uid || null,
                    email: auth.currentUser?.email || null,
                });
            } catch { }

            return {
                ok: true,
                businessId,
                published: false,
                needsReview: true,
                reviewStatus: "pending_review",
                status: "pending_review",
            };
        }

        await updateDoc(r, {
            published: false,
            needsReview: false,
            reviewStatus: "draft",
            reviewReason: "",
            status: "owner_draft",
            submittedForReviewAt: null,
            submittedForReviewBy: null,
            submittedForReviewByEmail: null,
            updatedAt: serverTimestamp(),
        });

        try {
            await logAuditEvent("business_unpublished_to_draft", {
                businessId,
                businessName,
                uid: auth.currentUser?.uid || null,
                email: auth.currentUser?.email || null,
            });
        } catch { }

        return {
            ok: true,
            businessId,
            published: false,
            needsReview: false,
            reviewStatus: "draft",
            status: "owner_draft",
        };
    }

    export async function submitBusinessForReview(businessId, extraUpdates = {}) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing business id");

        const r = dref(BUSINESSES_COLLECTION, businessId);
        const snap = await getDocSafe(r);
        if (!snap.exists()) throw new Error("Business not found");

        const biz = snap.data() || {};
        const businessName = biz.name || biz.businessName || "";

        const payload = {
            ...extraUpdates,
            published: false,
            needsReview: true,
            reviewStatus: "pending_review",
            reviewReason: "",
            status: "pending_review",
            submittedForReviewAt: serverTimestamp(),
            submittedForReviewBy: auth.currentUser?.uid || null,
            submittedForReviewByEmail: auth.currentUser?.email || null,
            approvedAt: null,
            approvedBy: null,
            approvedByEmail: null,
            rejectedAt: null,
            rejectedBy: null,
            rejectedByEmail: null,
            updatedAt: serverTimestamp(),
        };

        await updateDoc(r, payload);

        try {
            await logAuditEvent("business_submitted_for_review", {
                businessId,
                businessName,
                uid: auth.currentUser?.uid || null,
                email: auth.currentUser?.email || null,
                source: "submitBusinessForReview",
            });
        } catch { }

        return {
            ok: true,
            businessId,
            published: false,
            needsReview: true,
            reviewStatus: "pending_review",
            status: "pending_review",
        };
    }

    export async function approveBusiness(businessId, { note = "" } = {}) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing business id");
        if (!(await isAdminOrMod())) throw new Error("Permission denied (admin/mod required).");

        const r = dref(BUSINESSES_COLLECTION, businessId);
        const snap = await getDocSafe(r);
        if (!snap.exists()) throw new Error("Business not found");

        const biz = snap.data() || {};
        const businessName = biz.name || biz.businessName || "";

        const submittedChanges =
            biz.pendingChanges ||
            biz.lastSubmittedChanges ||
            biz.submittedChanges ||
            {};

        await updateDoc(r, {
            published: true,
            needsReview: false,
            reviewStatus: "approved",
            reviewReason: str(note).trim(),
            status: "published",
            approvedAt: serverTimestamp(),
            approvedBy: auth.currentUser?.uid || null,
            approvedByEmail: auth.currentUser?.email || null,
            updatedAt: serverTimestamp(),
        });

        try {
            await logAuditEvent("business_approved", {
                action: "business_approved",
                businessId,
                businessName,
                email: auth.currentUser?.email || "",
                details: {
                    businessId,
                    businessName,
                    approvedBy: auth.currentUser?.uid || null,
                    approvedByEmail: auth.currentUser?.email || "",
                    changes: submittedChanges,
                    timestamp: new Date().toISOString(),
                },
            });
        } catch { }

        return {
            ok: true,
            businessId,
            published: true,
            needsReview: false,
            reviewStatus: "approved",
            status: "published",
        };
    }

    export async function rejectBusiness(businessId, { reason = "" } = {}) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing business id");
        if (!(await isAdminOrMod())) throw new Error("Permission denied (admin/mod required).");

        const r = dref(BUSINESSES_COLLECTION, businessId);
        const snap = await getDocSafe(r);
        if (!snap.exists()) throw new Error("Business not found");

        const biz = snap.data() || {};
        const businessName = biz.name || biz.businessName || "";

        await updateDoc(r, {
            published: false,
            needsReview: false,
            reviewStatus: "rejected",
            reviewReason: str(reason).trim(),
            status: "rejected",
            rejectedAt: serverTimestamp(),
            rejectedBy: auth.currentUser?.uid || null,
            rejectedByEmail: auth.currentUser?.email || null,
            updatedAt: serverTimestamp(),
        });

        try {
            await logAuditEvent("business_rejected", {
                businessId,
                businessName,
                reason: str(reason).trim(),
                uid: auth.currentUser?.uid || null,
                email: auth.currentUser?.email || null,
            });
        } catch { }

        return {
            ok: true,
            businessId,
            published: false,
            needsReview: false,
            reviewStatus: "rejected",
            status: "rejected",
        };
    }

    /* ───────────────────────────── DELETE ───────────────────────────── */

    const _myBusinessesCache = new Map();
    const _favoriteIdsCache = new Map();
    const _filterOptionsCache = new Map();

    const MY_BUSINESSES_TTL_MS = 20 * 1000;
    const FAVORITES_TTL_MS = 15 * 1000;
    const FILTER_OPTIONS_TTL_MS = 60 * 1000;

    function getCacheHit(cache, key) {
        const hit = cache.get(key);
        if (!hit) return null;
        if (hit.exp <= Date.now()) {
            cache.delete(key);
            return null;
        }
        return hit.value;
    }

    function setCacheHit(cache, key, value, ttlMs) {
        cache.set(key, {
            value,
            exp: Date.now() + ttlMs,
        });
        return value;
    }

    function clearUserScopedCaches(uid = auth.currentUser?.uid, email = auth.currentUser?.email) {
        const safeUid = str(uid).trim();
        const safeEmail = lower(email || "");

        if (safeUid) {
            _myBusinessesCache.delete(`mybiz:${safeUid}:${safeEmail}`);
            _favoriteIdsCache.delete(`favids:${safeUid}`);
        }

        if (safeEmail) {
            _myBusinessesCache.delete(`mybiz:${safeUid}:${safeEmail}`);
        }
    }

    function clearFilterOptionsCache(country = "") {
        if (!country) {
            _filterOptionsCache.clear();
            return;
        }
        const normalized = normalizeCountryCode(country) || str(country).trim().toUpperCase();
        for (const key of _filterOptionsCache.keys()) {
            if (key.startsWith(`filters:${normalized}:`)) {
                _filterOptionsCache.delete(key);
            }
        }
    }

    export async function deleteBusinessById(idOrBiz) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in.");

        const businessId =
            typeof idOrBiz === "string" ? idOrBiz : idOrBiz?.id || idOrBiz?.businessId || "";
        if (!businessId) throw new Error("Missing business id.");

        const uid = user.uid;
        const email = lower(user.email);

        const bizRef = dref(BUSINESSES_COLLECTION, businessId);
        const claimRef = dref(CLAIMS_COLLECTION, businessId);

        const bizSnap = await getDocSafe(bizRef);
        if (!bizSnap.exists()) throw new Error("Business not found.");

        const biz = bizSnap.data() || {};
        const ownerUid = biz.ownerUid || null;

        let roles = [];
        try {
            const uSnap = await tryGetDoc(dref(USERS_COLLECTION, uid));
            const uData = uSnap?.exists?.() ? uSnap.data() : {};
            roles = normalizeRolesArray(uData?.roles || uData?.role).filter(
                (r) => lower(r) !== "government"
            );
        } catch {
            roles = [];
        }

        const isAdmin = roles.map(lower).includes("admin");
        const isOwner = ownerUid && ownerUid === uid;

        if (!isAdmin && !isOwner) {
            const err = new Error("permission-denied");
            err.code = "permission-denied";
            throw err;
        }

        try {
            await logAuditEvent("business_deleted", {
                businessId,
                deletedByUid: uid,
                deletedByEmail: email || null,
                ownerUid: ownerUid || null,
                roles,
            });
        } catch { }

        try {
            await deleteStoragePath(`businesses/${businessId}/`);
        } catch { }

        const bizData = bizSnap.data();

        try {
            const key = `mybiz:archived:${uid}`;
            const existing = JSON.parse(localStorage.getItem(key) || "[]");

            const archivedEntry = {
                id: businessId,
                name: bizData?.name || "",
                category: bizData?.category || "",
                state: bizData?.state || "",
                town: bizData?.town || "",
                deletedAt: Date.now(),
            };

            const updated = [...existing, archivedEntry];
            localStorage.setItem(key, JSON.stringify(updated));
            console.log("[History] Archived business saved:", archivedEntry);
        } catch (e) {
            console.warn("[History] Failed to archive business:", e);
        }

        await deleteDoc(bizRef);

        try {
            await deleteDoc(claimRef);
        } catch {
            try {
                await setDoc(
                    claimRef,
                    {
                        revokedAt: serverTimestamp(),
                        revokedByUid: uid,
                        revokedByEmail: email || null,
                    },
                    { merge: true }
                );
            } catch { }
        }

        try {
            await updateDoc(dref(USERS_COLLECTION, uid), {
                businessSlots: increment(1),
                updatedAt: serverTimestamp(),
            });
            console.log("[deleteBusinessById] slot restored +1");
        } catch (e) {
            console.warn("[deleteBusinessById] failed to restore slot:", e);
        }

        clearUserScopedCaches(uid, email);
        clearFilterOptionsCache(bizData?.country || "US");

        return true;
    }

    export async function deleteBusinesses(ids = []) {
        const unique = [...new Set((ids || []).map((x) => str(x).trim()).filter(Boolean))];
        if (!unique.length) return { ok: true, results: [] };

        const results = await Promise.allSettled(unique.map((id) => deleteBusinessById(id)));

        const out = results.map((r, i) => {
            const id = unique[i];
            if (r.status === "fulfilled") return { id, ok: true };
            return { id, ok: false, error: explainError(r.reason) };
        });

        const failed = out.filter((x) => !x.ok);
        return { ok: failed.length === 0, results: out, failed };
    }

    /* ───────────────────────────── claim / ownership ───────────────────────────── */
    export async function claimBusiness(businessId, userEmail) {
        await assertNotInMaintenance();

        const user = auth.currentUser || null;
        const email = user?.email || str(userEmail).trim();
        if (!email) throw new Error("Must be signed in (or provide an email) to claim.");

        const r = dref(CLAIMS_COLLECTION, businessId);

        await setDoc(
            r,
            {
                claimedBy: email,
                uid: user?.uid || null,
                claimedAt: serverTimestamp(),
                revokedAt: null,
            },
            { merge: true }
        );

        clearUserScopedCaches(user?.uid, email);

        return {
            ok: true,
            businessId,
            claimedBy: email,
            refPath: `${CLAIMS_COLLECTION}/${businessId}`,
        };
    }

    export async function unclaimBusiness(businessId) {
        await assertNotInMaintenance();
        const uid = auth.currentUser?.uid || null;
        const email = auth.currentUser?.email || null;

        await setDoc(
            dref(CLAIMS_COLLECTION, businessId),
            { revokedAt: serverTimestamp() },
            { merge: true }
        );

        clearUserScopedCaches(uid, email);

        return { ok: true, businessId, refPath: `${CLAIMS_COLLECTION}/${businessId}` };
    }

    export async function ensureClaimOrOwnership(businessId) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in.");

        const uid = user.uid;
        const email = lower(user.email);

        await setDoc(
            dref(CLAIMS_COLLECTION, businessId),
            { uid, claimedBy: email, claimedAt: serverTimestamp(), revokedAt: null },
            { merge: true }
        );

        const bRef = dref(BUSINESSES_COLLECTION, businessId);
        const bSnap = await getDocSafe(bRef);
        if (bSnap.exists() && !bSnap.data()?.ownerUid) {
            await updateDoc(bRef, { ownerUid: uid, claimedBy: email });
        }

        clearUserScopedCaches(uid, email);

        return true;
    }

    // ✅ Get single business by ID
    // Used by src/pages/VerifyOwnership.jsx
    export async function getBusinessDataById(businessId) {
        const id = str(businessId).trim();
        if (!id) throw new Error("Missing business id");

        const snap = await getDocSafe(dref(BUSINESSES_COLLECTION, id));

        if (!snap.exists()) {
            throw new Error("Business not found");
        }

        return withDefaults({
            id: snap.id,
            ...snap.data(),
        });
    }

    export async function getBusinessesOwnedByUid(uid = auth.currentUser?.uid) {
        if (!uid) return [];
        const snap = await getDocsSafe(
            query(col(BUSINESSES_COLLECTION), where("ownerUid", "==", uid), fsLimit(50))
        );
        return mapDocs(snap).map(withDefaults);
    }

    export async function getBusinessesClaimedByEmail(email) {
        if (!email) return [];
        try {
            const claimSnap = await tryGetDocs(
                query(col(CLAIMS_COLLECTION), where("claimedBy", "==", email), fsLimit(100))
            );
            if (!claimSnap) return [];

            const ids = [
                ...new Set(claimSnap.docs.filter((d) => !d.data()?.revokedAt).map((d) => d.id)),
            ];
            if (!ids.length) return [];

            const out = [];
            for (const part of chunk(ids, 10)) {
                const snap = await getDocsSafe(
                    query(col(BUSINESSES_COLLECTION), where(documentId(), "in", part))
                );
                out.push(...snap.docs.map((d) => withDefaults({ id: d.id, ...d.data() })));
            }
            return out;
        } catch (e) {
            if (isPermDenied(e)) return [];
            throw e;
        }
    }

    export async function getMyBusinesses({ force = false } = {}) {
        const uid = auth.currentUser?.uid || null;
        const email = lower(auth.currentUser?.email || "");
        if (!uid && !email) return [];

        const cacheKey = `mybiz:${uid || ""}:${email || ""}`;
        if (!force) {
            const cached = getCacheHit(_myBusinessesCache, cacheKey);
            if (cached) return cached;
        }

        const [owned, claimed] = await Promise.all([
            uid ? getBusinessesOwnedByUid(uid) : [],
            email ? getBusinessesClaimedByEmail(email) : [],
        ]);

        const m = new Map();
        [...owned, ...claimed].forEach((b) => m.set(b.id, b));

        return setCacheHit(_myBusinessesCache, cacheKey, [...m.values()], MY_BUSINESSES_TTL_MS);
    }

    /* ───────────────────────────── favorites ───────────────────────────── */
    function favoriteDocRef(uid = auth.currentUser?.uid) {
        if (!uid) throw new Error("You must be signed in.");
        return dref(USER_FAVORITES_COLLECTION, uid);
    }

    export async function getMyFavoriteIds(uid = auth.currentUser?.uid, { force = false } = {}) {
        if (!uid) return [];

        const cacheKey = `favids:${uid}`;
        if (!force) {
            const cached = getCacheHit(_favoriteIdsCache, cacheKey);
            if (cached) return cached;
        }

        try {
            const snap = await tryGetDoc(favoriteDocRef(uid));
            if (!snap?.exists?.()) {
                return setCacheHit(_favoriteIdsCache, cacheKey, [], FAVORITES_TTL_MS);
            }

            const data = snap.data() || {};
            const ids = [
                ...new Set(
                    (Array.isArray(data.businessIds) ? data.businessIds : [])
                        .map((x) => str(x).trim())
                        .filter(Boolean)
                ),
            ];

            return setCacheHit(_favoriteIdsCache, cacheKey, ids, FAVORITES_TTL_MS);
        } catch (e) {
            if (isPermDenied(e)) return [];
            throw e;
        }
    }

    export async function isBusinessFavorited(businessId, uid = auth.currentUser?.uid) {
        if (!uid || !businessId) return false;
        const ids = await getMyFavoriteIds(uid);
        return ids.includes(str(businessId).trim());
    }

    export async function addBusinessToFavorites(businessId, meta = {}) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in to save favorites.");

        const id = str(businessId).trim();
        if (!id) throw new Error("Missing business id");

        const already = await isBusinessFavorited(id, user.uid);
        if (already) {
            return { ok: true, businessId: id, favorited: true, already: true };
        }

        const favRef = favoriteDocRef(user.uid);

        await setDoc(
            favRef,
            {
                uid: user.uid,
                email: user.email || "",
                businessIds: arrayUnion(id),
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp(),
            },
            { merge: true }
        );

        _favoriteIdsCache.delete(`favids:${user.uid}`);

        try {
            await updateDoc(businessRef(id), {
                saveCount: increment(1),
                updatedAt: serverTimestamp(),
            });
        } catch { }

        try {
            await logAuditEvent("favorite_added", {
                businessId: id,
                uid: user.uid,
                email: user.email || null,
                businessName: str(meta?.businessName || "").trim() || null,
            });
        } catch { }

        return { ok: true, businessId: id, favorited: true };
    }

    export async function removeBusinessFromFavorites(businessId, meta = {}) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in to edit favorites.");

        const id = str(businessId).trim();
        if (!id) throw new Error("Missing business id");

        const already = await isBusinessFavorited(id, user.uid);
        if (!already) {
            return { ok: true, businessId: id, favorited: false, alreadyRemoved: true };
        }

        const favRef = favoriteDocRef(user.uid);

        await setDoc(
            favRef,
            {
                uid: user.uid,
                email: user.email || "",
                businessIds: arrayRemove(id),
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );

        _favoriteIdsCache.delete(`favids:${user.uid}`);

        try {
            const snap = await getDocSafe(businessRef(id));
            if (snap.exists()) {
                const current = toNumber(snap.data()?.saveCount, 0);
                await updateDoc(businessRef(id), {
                    saveCount: Math.max(0, current - 1),
                    updatedAt: serverTimestamp(),
                });
            }
        } catch { }

        try {
            await logAuditEvent("favorite_removed", {
                businessId: id,
                uid: user.uid,
                email: user.email || null,
                businessName: str(meta?.businessName || "").trim() || null,
            });
        } catch { }

        return { ok: true, businessId: id, favorited: false };
    }

    export async function toggleBusinessFavorite(businessId, meta = {}) {
        const already = await isBusinessFavorited(businessId);
        if (already) return removeBusinessFromFavorites(businessId, meta);
        return addBusinessToFavorites(businessId, meta);
    }

    export async function getMyFavoriteBusinesses(uid = auth.currentUser?.uid, { force = false } = {}) {
        const ids = await getMyFavoriteIds(uid, { force });
        if (!ids.length) return [];

        const out = [];
        for (const part of chunk(ids, 10)) {
            const snap = await getDocsSafe(
                query(col(BUSINESSES_COLLECTION), where(documentId(), "in", part))
            );

            out.push(
                ...snap.docs
                    .map((d) => withDefaults({ id: d.id, ...d.data() }))
                    .filter((b) => !!b?.id)
            );
        }

        const orderMap = new Map(ids.map((id, idx) => [id, idx]));
        out.sort((a, b) => (orderMap.get(a.id) ?? 999999) - (orderMap.get(b.id) ?? 999999));

        return out;
    }

    export function subscribeToMyFavoriteIds(onNext, onError, uid = auth.currentUser?.uid) {
        if (!uid) {
            onNext?.([]);
            return () => { };
        }

        return subscribeDoc(
            favoriteDocRef(uid),
            (snap) => {
                const data = snap?.exists?.() ? snap.data() || {} : {};
                const ids = [
                    ...new Set(
                        (Array.isArray(data.businessIds) ? data.businessIds : [])
                            .map((x) => str(x).trim())
                            .filter(Boolean)
                    ),
                ];

                setCacheHit(_favoriteIdsCache, `favids:${uid}`, ids, FAVORITES_TTL_MS);
                onNext?.(ids);
            },
            onError
        );
    }

    /* ───────────────────────────── business analytics / reviews ───────────────────────────── */
    export async function incrementBusinessView(businessId) {
        await assertNotInMaintenance();

        const id = str(businessId).trim();
        if (!id) throw new Error("Missing business id");

        const snap = await getDocSafe(businessRef(id));
        if (!snap.exists()) throw new Error("Business not found");

        const biz = snap.data() || {};

        const currentUid = auth.currentUser?.uid || null;
        const currentEmail = lower(auth.currentUser?.email || "");

        const currentRoles = await getCurrentUserRolesSafe();
        const isInternalViewer =
            currentRoles.includes("admin") ||
            currentRoles.includes("moderator") ||
            currentRoles.includes("mod");

        if (isInternalViewer) {
            return {
                ok: true,
                businessId: id,
                skipped: true,
                reason: "internal-admin-mod-view",
            };
        }

        const ownerUid = str(biz.ownerUid).trim();
        const claimedBy = lower(biz.claimedBy || "");

        let claimUid = "";
        let claimEmail = "";

        try {
            const claimSnap = await tryGetDoc(dref(CLAIMS_COLLECTION, id));
            if (claimSnap?.exists?.()) {
                const claimData = claimSnap.data() || {};
                if (!claimData?.revokedAt) {
                    claimUid = str(claimData.uid).trim();
                    claimEmail = lower(claimData.claimedBy || "");
                }
            }
        } catch { }

        const isOwnerByUid =
            !!currentUid &&
            ((!!ownerUid && currentUid === ownerUid) || (!!claimUid && currentUid === claimUid));

        const isOwnerByEmail =
            !!currentEmail &&
            ((!!claimedBy && currentEmail === claimedBy) ||
                (!!claimEmail && currentEmail === claimEmail));

        if (isOwnerByUid || isOwnerByEmail) {
            return {
                ok: true,
                businessId: id,
                skipped: true,
                reason: "owner-self-view",
            };
        }

        await updateDoc(businessRef(id), {
            viewCount: increment(1),
            updatedAt: serverTimestamp(),
        });

        try {
            await logAuditEvent("business_view_incremented", {
                businessId: id,
                uid: currentUid,
                email: currentEmail || null,
            });
        } catch { }

        return { ok: true, businessId: id, skipped: false };
    }

    export async function recalculateBusinessRating(businessId) {
        const id = str(businessId).trim();
        if (!id) throw new Error("Missing business id");

        const snap = await getDocsSafe(businessReviewsCol(id));
        const reviews = snap.docs
            .map((d) => ({ id: d.id, ...(d.data() || {}) }))
            .filter((r) => r.active !== false && lower(r.status || "active") !== "removed");

        const reviewCount = reviews.length;
        const ratingSum = reviews.reduce((sum, r) => sum + normalizeRating(r.rating), 0);
        const averageRating = reviewCount > 0 ? Number((ratingSum / reviewCount).toFixed(2)) : 0;

        await updateDoc(businessRef(id), {
            averageRating,
            reviewCount,
            updatedAt: serverTimestamp(),
        });

        return {
            ok: true,
            businessId: id,
            averageRating,
            reviewCount,
        };
    }

    export async function submitBusinessReview({
        businessId,
        rating,
        text = "",
        displayName = "",
    } = {}) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in to leave a review.");

        const id = str(businessId).trim();
        if (!id) throw new Error("Missing business id");

        const cleanRating = normalizeRating(rating);
        if (!cleanRating) throw new Error("Rating must be between 1 and 5.");

        const reviewRef = businessReviewRef(id, user.uid);
        const reviewSnap = await tryGetDoc(reviewRef);
        const existed = !!reviewSnap?.exists?.();

        await setDoc(
            reviewRef,
            {
                uid: user.uid,
                email: user.email || "",
                displayName: str(displayName).trim() || user.displayName || "User",
                rating: cleanRating,
                text: str(text).trim(),
                active: true,
                status: "active",
                createdAt: existed ? reviewSnap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );

        const totals = await recalculateBusinessRating(id);

        try {
            await logAuditEvent(existed ? "business_review_updated" : "business_review_created", {
                businessId: id,
                uid: user.uid,
                email: user.email || null,
                rating: cleanRating,
            });
        } catch { }

        return {
            ok: true,
            businessId: id,
            reviewId: user.uid,
            ...totals,
        };
    }

    export async function removeBusinessReview(businessId, reviewId = auth.currentUser?.uid) {
        await assertNotInMaintenance();

        const user = auth.currentUser;
        const id = str(businessId).trim();
        const rid = str(reviewId).trim();

        if (!id) throw new Error("Missing business id");
        if (!rid) throw new Error("Missing review id");

        const adminMod = await isAdminOrMod();
        if (!user) throw new Error("You must be signed in.");
        if (!adminMod && user.uid !== rid) {
            throw new Error("You don't have permission to remove this review.");
        }

        await updateDoc(businessReviewRef(id, rid), {
            active: false,
            status: "removed",
            updatedAt: serverTimestamp(),
        });

        const totals = await recalculateBusinessRating(id);

        try {
            await logAuditEvent("business_review_removed", {
                businessId: id,
                reviewId: rid,
                uid: user.uid,
                email: user.email || null,
            });
        } catch { }

        return {
            ok: true,
            businessId: id,
            reviewId: rid,
            ...totals,
        };
    }

    export async function getBusinessReviews(businessId, { includeInactive = false } = {}) {
        const id = str(businessId).trim();
        if (!id) return [];

        const snap = await getDocsSafe(query(businessReviewsCol(id), orderBy("updatedAt", "desc")));
        const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));

        return items.filter((r) =>
            includeInactive ? true : r.active !== false && lower(r.status || "active") !== "removed"
        );
    }

    export function subscribeToBusinessReviews(
        businessId,
        onNext = () => { },
        onError = () => { },
        { includeInactive = false } = {}
    ) {
        const id = str(businessId).trim();
        if (!id) {
            onNext?.([]);
            return () => { };
        }

        return onSnapshot(
            query(businessReviewsCol(id), orderBy("updatedAt", "desc")),
            (snap) => {
                const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
                const filtered = items.filter((r) =>
                    includeInactive ? true : r.active !== false && lower(r.status || "active") !== "removed"
                );
                onNext?.(filtered);
            },
            onError
        );
    }

    /* ───────────────────────────── subscribe to single business ───────────────────────────── */
    export function subscribeToBusinessById(businessId, onNext, onError) {
        if (!businessId) return () => { };
        const r = dref(BUSINESSES_COLLECTION, businessId);
        return subscribeDoc(
            r,
            (snap) => {
                if (!snap.exists()) return onNext?.(null);
                onNext?.(withDefaults({ id: snap.id, ...snap.data() }));
            },
            onError
        );
    }

    /* ───────────────────────────── pagination (dashboard search) ───────────────────────────── */
    export async function fetchBusinessPage({
        country = "",
        limit = 50,
        startAfterDoc = null,
        includeDrafts = false,
    } = {}) {
        const constraints = [];

        const c = normalizeCountryCode(country);
        if (c) constraints.push(where("country", "==", c));

        if (!includeDrafts) {
            constraints.push(where("published", "==", true));
        }

        const safeLimit = Math.min(Math.max(1, Number(limit) || 50), 50);

        let qRef = query(
            col(BUSINESSES_COLLECTION),
            ...constraints,
            orderBy("nameLower"),
            fsLimit(safeLimit)
        );

        if (startAfterDoc) {
            qRef = query(
                col(BUSINESSES_COLLECTION),
                ...constraints,
                orderBy("nameLower"),
                fsStartAfter(startAfterDoc),
                fsLimit(safeLimit)
            );
        }

        const snap = await getDocsSafe(qRef);
        const items = snap.docs.map((d) => withDefaults({ id: d.id, ...d.data() }));
        const last = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

        return { items, lastDoc: last, size: snap.size };
    }

    /* ───────────────────────────── LIVE SEARCH (PUBLIC) ───────────────────────────── */
    export function subscribeToBusinessSearch(
        filters = {},
        onNext = () => { },
        onError = () => { },
        options = {}
    ) {
        try {
            const f = filters || {};
            const opt = options || {};

            const includeDrafts = opt.includeDrafts === true;
            const requireCountry = opt.requireCountry !== false;

            const requestedLimit = Number.isFinite(Number(opt.limit)) ? Number(opt.limit) : 50;
            const limitNum = Math.min(Math.max(1, requestedLimit), 50);

            const country = str(f.country);
            if (requireCountry && !country) {
                const err = new Error("Country is required for search.");
                try {
                    onError(err);
                } catch { }
                return () => { };
            }

            const base = col(BUSINESSES_COLLECTION);
            const constraints = [];

            if (!includeDrafts) constraints.push(where("published", "==", true));
            if (country) constraints.push(where("country", "==", country));

            if (str(f.category)) constraints.push(where("category", "==", str(f.category)));
            if (str(f.state)) constraints.push(where("state", "==", str(f.state)));
            if (str(f.county)) constraints.push(where("county", "==", str(f.county)));
            if (str(f.town)) constraints.push(where("town", "==", str(f.town)));
            if (str(f.zipCode)) constraints.push(where("zipCode", "==", str(f.zipCode)));

            if (f.openNow === true) constraints.push(where("isOpenNow", "==", true));
            if (str(f.status)) constraints.push(where("status", "==", str(f.status)));

            const name = lower(f.name);
            const doNamePrefix = !!name;

            let qRef;

            if (doNamePrefix) {
                const start = name;
                const end = name + "\uf8ff";
                qRef = query(
                    base,
                    ...constraints,
                    orderBy("nameLower"),
                    startAt(start),
                    endAt(end),
                    fsLimit(limitNum)
                );
            } else {
                qRef = query(base, ...constraints, orderBy("nameLower"), fsLimit(limitNum));
            }

            return onSnapshot(
                qRef,
                (snap) => {
                    try {
                        onNext?.(snap);
                    } catch { }
                },
                (err) => {
                    try {
                        onError?.(err);
                    } catch { }
                }
            );
        } catch (e) {
            try {
                onError?.(e);
            } catch { }
            return () => { };
        }
    }

    /* ───────────────────────────── filter options ───────────────────────────── */
    export async function loadFilterOptionsFromBusinesses(country = "US", options = {}) {
        const includeDrafts = options.includeDrafts === true;
        const short = normalizeCountryCode(country) || "US";
        const requestedScanLimit = Number.isFinite(Number(options.scanLimit))
            ? Number(options.scanLimit)
            : 100;
        const scanLimit = Math.min(Math.max(1, requestedScanLimit), 100);

        const cacheKey = `filters:${short}:${includeDrafts ? "all" : "published"}:${scanLimit}`;
        if (options.force !== true) {
            const cached = getCacheHit(_filterOptionsCache, cacheKey);
            if (cached) return cached;
        }

        const base = col(BUSINESSES_COLLECTION);

        const collect = (snap) => {
            const sets = {
                category: new Set(),
                state: new Set(),
                county: new Set(),
                town: new Set(),
            };

            const raw = [];

            snap.forEach((d) => {
                const v = d.data() || {};

                const entry = {
                    category: normalizeCategory(v.category, { allowOther: true }) || "",
                    state: str(v.state).trim(),
                    county: str(v.county).trim(),
                    town: str(v.town).trim(),
                    country: normalizeCountryCode(v.country) || "",
                };

                raw.push(entry);

                if (entry.category) sets.category.add(entry.category);
                if (entry.state) sets.state.add(entry.state);
                if (entry.county) sets.county.add(entry.county);
                if (entry.town) sets.town.add(entry.town);
            });

            return {
                category: [...sets.category].filter(Boolean).sort(),
                state: [...sets.state].filter(Boolean).sort(),
                county: [...sets.county].filter(Boolean).sort(),
                town: [...sets.town].filter(Boolean).sort(),
                raw,
            };
        };

        try {
            const constraints = [];
            if (short) constraints.push(where("country", "==", short));
            if (!includeDrafts) constraints.push(where("published", "==", true));

            const qRef = constraints.length
                ? query(base, ...constraints, fsLimit(scanLimit))
                : query(base, fsLimit(scanLimit));

            const snap = await getDocsSafe(qRef);
            const result = collect(snap);
            return setCacheHit(_filterOptionsCache, cacheKey, result, FILTER_OPTIONS_TTL_MS);
        } catch (e) {
            if (isPermDenied(e)) {
                return { category: [], state: [], county: [], town: [], raw: [] };
            }
            throw e;
        }
    }

    /* ───────────────────────────── REPORTS (legacy optional) ───────────────────────────── */
    export async function submitBusinessReport({
        businessId,
        businessName,
        reason = "spam",
        details = "",
        reporterUid = null,
        reporterEmail = null,
        reporterRole = "public",
    } = {}) {
        await assertNotInMaintenance();

        const bizId = str(businessId).trim();
        if (!bizId) throw new Error("Missing businessId");

        const payload = {
            businessId: bizId,
            businessName: str(businessName).trim(),
            reason: str(reason).trim() || "other",
            details: str(details).trim(),
            status: "open",
            reporterUid: reporterUid || auth.currentUser?.uid || null,
            reporterEmail: str(reporterEmail || auth.currentUser?.email || "").trim() || null,
            reporterRole: str(reporterRole || "public").trim() || "public",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        const refDoc = await addDoc(col(REPORTS_COLLECTION), payload);

        try {
            await logAuditEvent("report_submitted", {
                reportId: refDoc.id,
                businessId: bizId,
                reason: payload.reason,
                reporterUid: payload.reporterUid,
                reporterEmail: payload.reporterEmail,
                reporterRole: payload.reporterRole,
            });
        } catch { }

        return { ok: true, reportId: refDoc.id };
    }

    /* ───────────────────────────── STORAGE: upload / delete ───────────────────────────── */
    function ensureSignedIn() {
        const u = auth.currentUser;
        if (!u) throw new Error("You must be signed in.");
        return u;
    }

    function safeFileName(name = "") {
        return String(name || "file")
            .replace(/\s+/g, "_")
            .replace(/[^a-zA-Z0-9._-]/g, "")
            .slice(0, 80);
    }

    function extFromTypeOrName(file) {
        const n = String(file?.name || "");
        const m = n.match(/\.([a-zA-Z0-9]+)$/);
        if (m && m[1]) return m[1].toLowerCase();

        const t = String(file?.type || "").toLowerCase();
        if (t.includes("png")) return "png";
        if (t.includes("jpeg") || t.includes("jpg")) return "jpg";
        if (t.includes("webp")) return "webp";
        if (t.includes("gif")) return "gif";
        return "bin";
    }

    function imageContentType(file) {
        const t = String(file?.type || "").toLowerCase();
        if (t.startsWith("image/")) return t;
        const ext = extFromTypeOrName(file);
        if (ext === "png") return "image/png";
        if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
        if (ext === "webp") return "image/webp";
        if (ext === "gif") return "image/gif";
        return "application/octet-stream";
    }

    const looksLikeFolderPath = (path) => {
        const p = safeTrim(path);
        if (!p) return false;
        if (p.endsWith("/")) return true;
        const last = p.split("/").pop() || "";
        return !/\.[a-z0-9]{2,6}$/i.test(last);
    };

    function downloadUrlToPath(u = "") {
        const url = String(u || "").trim();
        if (!/^https?:\/\//i.test(url)) return "";
        try {
            const m = url.match(/\/o\/([^?]+)\?/);
            if (!m || !m[1]) return "";
            return decodeURIComponent(m[1]);
        } catch {
            return "";
        }
    }

    function gsUrlToPath(gs = "") {
        const v = String(gs || "").trim();
        if (!v.toLowerCase().startsWith("gs://")) return "";
        const without = v.slice(5);
        const firstSlash = without.indexOf("/");
        if (firstSlash === -1) return "";
        return without.slice(firstSlash + 1);
    }

    /**
     * ✅ Backwards-compatible uploadBusinessImage
     * Supports BOTH:
     * 1) uploadBusinessImage({ businessId, file, kind, onProgress })
     * 2) uploadBusinessImage(businessId, kind, file)
     */
    export async function uploadBusinessImage(arg1, arg2, arg3) {
        if (typeof arg1 === "string") {
            return uploadBusinessImageImpl({
                businessId: arg1,
                kind: arg2 || "photos",
                file: arg3,
            });
        }

        return uploadBusinessImageImpl(arg1 || {});
    }

    async function uploadBusinessImageImpl({ businessId, file, kind = "photos", onProgress } = {}) {
        await assertNotInMaintenance();
        if (!STORAGE_ENABLED) throw new Error("Storage is disabled in this build.");
        if (!businessId) throw new Error("Missing businessId");
        if (!file) throw new Error("Missing file");

        ensureSignedIn();

        const ts = Date.now();
        const k = String(kind || "photos").toLowerCase();
        const folder = k === "logo" ? "logo" : k === "banner" ? "banner" : "photos";

        const safeOriginal = safeFileName(file.name);
        const base = String(safeOriginal).replace(/\.[a-z0-9]+$/i, "") || "image";
        const ext = extFromTypeOrName(file);

        const path = `businesses/${businessId}/${folder}/${ts}_${base}.${ext}`;

        const refObj = storageRef(storage, path);
        const meta = {
            contentType: imageContentType(file),
            customMetadata: {
                businessId: String(businessId),
                uploadedBy: String(auth.currentUser?.uid || ""),
                uploadedByEmail: String(auth.currentUser?.email || ""),
                kind: String(kind),
            },
        };

        const task = uploadBytesResumable(refObj, file, meta);

        const rawUrl = await new Promise((resolve, reject) => {
            task.on(
                "state_changed",
                (snap) => {
                    if (typeof onProgress === "function") {
                        const pct = snap.totalBytes
                            ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                            : 0;
                        onProgress(pct, snap);
                    }
                },
                (err) => reject(err),
                async () => {
                    try {
                        const u = await getDownloadURL(task.snapshot.ref);
                        resolve(u);
                    } catch (e) {
                        reject(e);
                    }
                }
            );
        });

        const freshUrl = addCacheBust(rawUrl, ts);

        invalidateCachedUrl(path);
        _urlCache.set(path, { url: freshUrl, exp: Date.now() + 15 * 60 * 1000 });

        try {
            await logAuditEvent("business_image_uploaded", {
                businessId,
                kind,
                path,
                uid: auth.currentUser?.uid || null,
                email: auth.currentUser?.email || null,
            });
        } catch { }

        return { path, url: freshUrl };
    }

    export async function deleteStoragePath(input) {
        await assertNotInMaintenance();
        if (!STORAGE_ENABLED) return { ok: false, disabled: true };
        if (!input) return { ok: true, skipped: "empty" };

        let p = safeTrim(input);

        if (isHttpUrl(p)) {
            const fromUrl = downloadUrlToPath(p);
            if (!fromUrl) return { ok: false, error: "Could not derive storage path from URL." };
            p = fromUrl;
        }

        if (p.toLowerCase().startsWith("gs://")) {
            const fromGs = gsUrlToPath(p);
            if (!fromGs) return { ok: false, error: "Could not derive storage path from gs:// URL." };
            p = fromGs;
        }

        try {
            if (looksLikeFolderPath(p)) {
                const folderRef = storageRef(storage, p.endsWith("/") ? p : `${p}/`);
                const res = await listAll(folderRef);
                const items = res?.items || [];

                const results = await Promise.allSettled(items.map((itemRef) => deleteObject(itemRef)));

                for (const itemRef of items) {
                    const full = itemRef?.fullPath || "";
                    if (full) invalidateCachedUrl(full);
                }

                const failed = results
                    .map((r, i) =>
                        r.status === "rejected" ? { path: items[i]?.fullPath, error: r.reason } : null
                    )
                    .filter(Boolean);

                const permFail = failed.find((f) =>
                    String(f?.error?.code || f?.error?.message || "").includes("permission")
                );
                if (permFail) throw permFail.error;

                try {
                    await logAuditEvent("business_image_folder_deleted", {
                        path: p,
                        count: items.length,
                        failedCount: failed.length,
                        uid: auth.currentUser?.uid || null,
                        email: auth.currentUser?.email || null,
                    });
                } catch { }

                return { ok: true, folder: true, deleted: items.length - failed.length, failed };
            }

            await deleteObject(storageRef(storage, p));
            invalidateCachedUrl(p);

            try {
                await logAuditEvent("business_image_deleted", {
                    path: p,
                    uid: auth.currentUser?.uid || null,
                    email: auth.currentUser?.email || null,
                });
            } catch { }

            return { ok: true, folder: false, deleted: 1, path: p };
        } catch (e) {
            const msg = String(e?.code || e?.message || "");
            if (msg.includes("object-not-found")) {
                invalidateCachedUrl(p);
                return { ok: true, alreadyMissing: true, path: p };
            }
            return { ok: false, error: explainError(e), raw: msg };
        }
    }

    /* ───────────────────────────── logo/banner setters + CLEARERS ───────────────────────────── */
    export async function setBusinessLogo(businessId, { path, url } = {}) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing businessId");

        const patch = {
            logoPath: str(path),
            logoUrl: str(url),
            updatedAt: serverTimestamp(),
        };

        await updateBusinessById(businessId, patch);
        return { ok: true, businessId, logoPath: patch.logoPath, logoUrl: patch.logoUrl };
    }

    export async function setBusinessBanner(businessId, { path, url } = {}) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing businessId");

        const patch = {
            bannerPath: str(path),
            bannerUrl: str(url),
            updatedAt: serverTimestamp(),
        };

        await updateBusinessById(businessId, patch);
        return { ok: true, businessId, bannerPath: patch.bannerPath, bannerUrl: patch.bannerUrl };
    }

    export async function clearBusinessLogo(businessId) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing businessId");
        await updateBusinessById(businessId, {
            logoPath: "",
            logoUrl: "",
            updatedAt: serverTimestamp(),
        });
        return { ok: true, businessId };
    }

    export async function clearBusinessBanner(businessId) {
        await assertNotInMaintenance();
        if (!businessId) throw new Error("Missing businessId");
        await updateBusinessById(businessId, {
            bannerPath: "",
            bannerUrl: "",
            updatedAt: serverTimestamp(),
        });
        return { ok: true, businessId };
    }

    export async function addBusinessPhoto(businessId, { path, url } = {}) {
        await assertNotInMaintenance();

        if (!businessId) throw new Error("Missing businessId");
        if (!path && !url) throw new Error("Missing photo path or URL");

        const bRef = dref(BUSINESSES_COLLECTION, businessId);
        const snap = await getDocSafe(bRef);

        if (!snap.exists()) throw new Error("Business not found");

        const cur = snap.data() || {};

        const cleanPath = str(path).trim();
        const cleanUrl = str(url).trim();

        const photoPaths = Array.isArray(cur.photoPaths)
            ? [...new Set(cur.photoPaths.filter(Boolean))]
            : [];

        const photoUrls = Array.isArray(cur.photoUrls)
            ? [...new Set(cur.photoUrls.filter(Boolean))]
            : [];

        // Firebase uploaded images ONLY use photoPaths
        if (cleanPath && !photoPaths.includes(cleanPath)) {
            photoPaths.push(cleanPath);
        }

        // External links ONLY use photoUrls
        if (
            cleanUrl &&
            !cleanUrl.includes("firebasestorage.googleapis.com") &&
            !cleanUrl.includes("storage.googleapis.com") &&
            !photoUrls.includes(cleanUrl)
        ) {
            photoUrls.push(cleanUrl);
        }

        await updateBusinessById(businessId, {
            photoPaths,
            photoUrls,
            updatedAt: serverTimestamp(),
        });

        return {
            ok: true,
            businessId,
            photoPaths,
            photoUrls,
        };
    }

    export async function removeBusinessPhoto(
        businessId,
        { path, deleteFromStorage = true } = {}
    ) {
        await assertNotInMaintenance();

        if (!businessId) throw new Error("Missing businessId");
        if (!path) throw new Error("Missing path");

        const bRef = dref(BUSINESSES_COLLECTION, businessId);
        const snap = await getDocSafe(bRef);

        if (!snap.exists()) throw new Error("Business not found");

        const cur = snap.data() || {};

        const cleanPath = str(path).trim();

        const photoPaths = Array.isArray(cur.photoPaths)
            ? cur.photoPaths.filter((p) => str(p).trim() !== cleanPath)
            : [];

        const photoUrls = Array.isArray(cur.photoUrls)
            ? cur.photoUrls.filter((u) => {
                const cleanUrl = str(u).trim();

                const cached = _urlCache.get(cleanPath)?.url || "";

                if (cached && cleanUrl === cached) {
                    return false;
                }

                return true;
            })
            : [];

        await updateBusinessById(businessId, {
            photoPaths,
            photoUrls,
            updatedAt: serverTimestamp(),
        });

        if (deleteFromStorage) {
            try {
                await deleteStoragePath(cleanPath);
            } catch { }
        }

        return {
            ok: true,
            businessId,
            photoPaths,
            photoUrls,
        };
    }
    export async function submitOwnerVerificationRequest({ businessId, last4SSN, notes }) {
        return {
            ok: false,
            disabled: true,
            message: "Owner verification (Business ID + SSN) is disabled in this build.",
        };
    }

    export async function listOwnerVerificationRequests({ status = "pending", limit = 200 } = {}) {
        return [];
    }

    export async function autoSeedBusinessesIfEmpty() {
        try {
            if (!IS_EMU) return false;
            const user = auth?.currentUser;
            if (!user) return false;

            const exists = await getDocsSafe(query(col(BUSINESSES_COLLECTION), fsLimit(1)));
            if (!exists.empty) return false;

            const uid = user.uid;
            const email = lower(user.email);

            const mk = (id, name, category, state, town, open = true) =>
                withDefaults({
                    id,
                    name,
                    nameLower: name.toLowerCase(),
                    category: normalizeCategory(category, { allowOther: true }),
                    state,
                    town,
                    country: "US",
                    isOpenNow: open,
                    ownerUid: uid,
                    verified: true,
                    accentColor: "#1E4D8B",
                    modules: {
                        featuredOffers: true,
                        meetTeam: false,
                        eventCalendar: false,
                    },
                    status: "published",
                    published: true,
                    needsReview: false,
                    reviewStatus: "approved",
                    viewCount: 0,
                    saveCount: 0,
                    averageRating: 0,
                    reviewCount: 0,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });

            const docs = [
                mk("demo-cafe", "Demo Cafe", "Cafe", "CA", "San Diego", true),
                mk("demo-salon", "Demo Salon", "Salon", "CA", "Chula Vista", false),
                mk("demo-auto", "Demo Auto Shop", "Auto Repair", "CA", "La Mesa", true),
                mk("demo-diner", "Demo Diner", "Restaurant", "CA", "El Cajon", true),
            ];

            for (const b of docs) {
                await setDoc(doc(db, BUSINESSES_COLLECTION, b.id), b, { merge: true });
                await setDoc(
                    doc(db, CLAIMS_COLLECTION, b.id),
                    { uid, claimedBy: email, claimedAt: serverTimestamp(), revokedAt: null },
                    { merge: true }
                );
            }

            clearUserScopedCaches(uid, email);
            clearFilterOptionsCache("US");

            console.info("[SEED] wrote", docs.length, "businesses + claims (emu)");
            return true;
        } catch (e) {
            if (!isPermDenied(e)) console.warn("[SEED] failed:", e?.message || e);
            return false;
        }
    }

    export async function logSearchQuery({ query: q, language, resultsCount }) {
        try {
            const user = auth?.currentUser || null;
            if (!user) return;
            await addDoc(collection(db, "searchLogs"), {
                query: str(q),
                language: str(language),
                resultsCount: Number.isFinite(resultsCount) ? resultsCount : 0,
                uid: user.uid,
                timestamp: serverTimestamp(),
            });
        } catch { }
    }

    export async function getVerificationLogs({
        search = "",
        status = "",
        limit = 250,
        startAfter = null,
    } = {}) {
        const AUDIT_COLLECTION = "auditLogs";

        const s = str(search).trim().toLowerCase();
        const st = str(status).trim().toLowerCase();
        const take = Number.isFinite(Number(limit)) ? Number(limit) : 250;

        const isVerificationAction = (action = "") => {
            const a = String(action || "").toLowerCase();

            return (
                a === "business_approved" ||
                a === "business_rejected" ||
                a === "business_submitted_for_review" ||
                a === "business_unpublished_to_draft" ||
                a.includes("review") ||
                a.includes("approved") ||
                a.includes("rejected") ||
                a.includes("verification") ||
                a.includes("verified") ||
                a.includes("unverified")
            );
        };

        const getRowStatus = (row) => {
            const action = String(row.action || "").toLowerCase();

            if (action === "business_approved") return "verified";
            if (action === "business_rejected") return "rejected";
            if (action === "business_submitted_for_review") return "pending";
            if (action === "business_unpublished_to_draft") return "draft";

            return str(
                row.status ||
                row.details?.status ||
                row.details?.reviewStatus ||
                row.details?.verificationStatus ||
                row.details?.toStatus ||
                row.details?.newStatus ||
                ""
            )
                .trim()
                .toLowerCase();
        };

        try {
            const base = collection(db, AUDIT_COLLECTION);

            let qRef = query(base, orderBy("timestamp", "desc"), fsLimit(Math.max(25, take)));

            if (startAfter) {
                qRef = query(
                    base,
                    orderBy("timestamp", "desc"),
                    fsStartAfter(startAfter),
                    fsLimit(Math.max(25, take))
                );
            }

            const snap = await getDocsSafe(qRef);

            const rows = snap.docs
                .map((d) => {
                    const v = d.data() || {};
                    const details = v.details || v.extra || v.meta || {};

                    return {
                        id: d.id,
                        ...v,
                        action: v.action || v.type || v.event || "",
                        timestamp: v.timestamp || v.createdAt || v.time || null,
                        details,

                        businessId:
                            v.businessId ||
                            v.targetBusinessId ||
                            v.targetId ||
                            details.businessId ||
                            details.targetBusinessId ||
                            details.bizId ||
                            details.target?.businessId ||
                            details.target?.id ||
                            "",

                        businessName:
                            v.businessName ||
                            details.businessName ||
                            details.name ||
                            details.targetBusinessName ||
                            "",
                    };
                })
                .filter((row) => isVerificationAction(row.action))
                .filter((row) => {
                    if (!st) return true;
                    return getRowStatus(row) === st;
                })
                .filter((row) => {
                    if (!s) return true;

                    const hay = [
                        row.action,
                        row.businessId,
                        row.businessName,
                        row.uid,
                        row.email,
                        row.actorEmail,
                        row.actorId,
                        row.details?.status,
                        row.details?.reviewStatus,
                        row.details?.verificationStatus,
                        row.details?.notes,
                        row.details?.reason,
                    ]
                        .map((x) => str(x).toLowerCase())
                        .join(" ");

                    return hay.includes(s);
                });

            const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;

            return {
                ok: true,
                items: rows,
                lastDoc,
                size: rows.length,
            };
        } catch (e) {
            if (isPermDenied(e)) {
                return {
                    ok: false,
                    items: [],
                    lastDoc: null,
                    size: 0,
                    error: "Permission denied by Firestore rules.",
                };
            }

            throw e;
        }
    }